import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { BETTIE_QUERY_KEYS } from '@pocapan/shared/domains/bettie/ui/queryKeys';
import { SCENE_QUERY_KEYS } from '@pocapan/shared/domains/scene/ui/queryKeys';

const STAR_HIDE_TIMEOUT = 2500;
const OPEN_NEXT_DELAY = 500;

interface UseOpenNextPreviewProps {
  handleOpenNextPreview: () => Promise<void>;
  isGenerating: boolean;
  hasEnoughHearts: boolean;
  contentsLength: number;
  onNeedGeneratingPopup: () => void;
  onNeedBettieSheet: () => void;
}

export const useOpenNextPreview = ({
  handleOpenNextPreview,
  isGenerating,
  hasEnoughHearts,
  contentsLength,
  onNeedGeneratingPopup,
  onNeedBettieSheet
}: UseOpenNextPreviewProps) => {
  const queryClient = useQueryClient();
  const [showStarForIndicesFrom, setShowStarForIndicesFrom] = useState<number | null>(null);
  const [isOpenNextPreviewCardLoading, setIsOpenNextPreviewCardLoading] = useState(false);
  const starHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
    };
  }, []);

  const handleOpenNextPreviewWithHeartCheck = async () => {
    if (isGenerating) {
      onNeedGeneratingPopup();
      return;
    }

    if (!hasEnoughHearts) {
      onNeedBettieSheet();
      return;
    }

    const startIndex = contentsLength;
    setIsOpenNextPreviewCardLoading(true);
    setTimeout(async () => {
      try {
        await handleOpenNextPreview();
        queryClient.invalidateQueries(BETTIE_QUERY_KEYS.bettieBalances);
        queryClient.invalidateQueries(SCENE_QUERY_KEYS.adaptedFictionPreviews);
        if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
        setShowStarForIndicesFrom(startIndex);
        starHideTimeoutRef.current = setTimeout(() => {
          setShowStarForIndicesFrom(null);
          starHideTimeoutRef.current = null;
        }, STAR_HIDE_TIMEOUT);
      } catch {
        // Failed to open next preview
      } finally {
        setIsOpenNextPreviewCardLoading(false);
      }
    }, OPEN_NEXT_DELAY);
  };

  return {
    showStarForIndicesFrom,
    isOpenNextPreviewCardLoading,
    handleOpenNextPreviewWithHeartCheck
  };
};
